export const apisData = {
    auth: {
        login: "/auth/login",
        logout: "/auth/logout",
        refresh: "/auth/refresh",
        me: "/auth/me",
        heartbeat: "/auth/heartbeat",
    },
    users: {
        list: "/users",
        listLite: "/users/list",
        create: "/users",
        detail: (id: string) => `/users/${id}`,
        update: (id: string) => `/users/${id}`,
        delete: (id: string) => `/users/${id}`,
        analytics: (id: string) => `/users/${id}/analytics`,
    },
    teams: {
        list: "/teams",
        listLite: "/teams/list",
        create: "/teams",
        detail: (id: string) => `/teams/${id}`,
        update: (id: string) => `/teams/${id}`,
        delete: (id: string) => `/teams/${id}`,
    },
    sprints: {
        list: "/sprints",
        create: "/sprints",
        detail: (id: string) => `/sprints/${id}`,
        update: (id: string) => `/sprints/${id}`,
        delete: (id: string) => `/sprints/${id}`,
        activate: (id: string) => `/sprints/${id}/activate`,
        summary: (id: string) => `/sprints/${id}/summary`,
        analytics: (id: string) => `/sprints/${id}/analytics`,
    },
    projects: {
        list: "/projects",
        listLite: "/projects/list",
        create: "/projects",
        detail: (id: string) => `/projects/${id}`,
        update: (id: string) => `/projects/${id}`,
        delete: (id: string) => `/projects/${id}`,
        sprints: (id: string) => `/projects/${id}/sprints`,
        analytics: (id: string) => `/projects/${id}/analytics`,
    },
    tasks: {
        list: (sprintId: string) => `/sprints/${sprintId}/tasks`,
        kanban: (sprintId: string) => `/sprints/${sprintId}/tasks/kanban`,
        pipeline: (sprintId: string) => `/sprints/${sprintId}/tasks/pipeline`,
        stats: (sprintId: string) => `/sprints/${sprintId}/tasks/stats`,
        create: (sprintId: string) => `/sprints/${sprintId}/tasks`,
        detail: (id: string) => `/tasks/${id}`,
        update: (id: string) => `/tasks/${id}`,
        updateStatus: (id: string) => `/tasks/${id}/status`,
        delete: (id: string) => `/tasks/${id}`,
        addTags: (id: string) => `/tasks/${id}/tags`,
        removeTag: (id: string, tag: string) => `/tasks/${id}/tags/${tag}`,
        addAttachment: (id: string) => `/tasks/${id}/attachments`,
        deleteAttachment: (id: string, attachmentId: string) => `/tasks/${id}/attachments/${attachmentId}`,
        removeAttachment: (id: string, attachmentId: string) => `/tasks/${id}/attachments/${attachmentId}`,
        transitionCriteria: (id: string) => `/tasks/${id}/transition-criteria`,
        markComplete: (id: string) => `/tasks/${id}/mark-complete`,
        listLite: "/tasks/list",
    },
    requirements: {
        list: (taskId: string) => `/tasks/${taskId}/requirements`,
        create: (taskId: string) => `/tasks/${taskId}/requirements`,
        update: (id: string) => `/requirements/${id}`,
        toggle: (id: string) => `/requirements/${id}/toggle`,
        delete: (id: string) => `/requirements/${id}`,
    },
    timeLogs: {
        sprintList: (sprintId: string) => `/sprints/${sprintId}/time-logs`,
        taskList: (taskId: string) => `/tasks/${taskId}/time-logs`,
        summary: (sprintId: string) => `/sprints/${sprintId}/time-logs/summary`,
        create: (taskId: string) => `/tasks/${taskId}/time-logs`,
        createStandalone: "/time-logs",
        update: (id: string) => `/time-logs/${id}`,
        delete: (id: string) => `/time-logs/${id}`,
    },
    time: {
        byProject: "/time/by-project",
        bySprint: "/time/by-sprint",
        byTeam: "/time/by-team",
        byMember: "/time/by-member",
        summary: "/time/summary",
    },
    blockers: {
        list: (sprintId: string) => `/sprints/${sprintId}/blockers`,
        analytics: (sprintId: string) => `/sprints/${sprintId}/blockers/analytics`,
        create: (sprintId: string) => `/sprints/${sprintId}/blockers`,
        detail: (id: string) => `/blockers/${id}`,
        update: (id: string) => `/blockers/${id}`,
        delete: (id: string) => `/blockers/${id}`,
        resolve: (id: string) => `/blockers/${id}/resolve`,
        escalate: (id: string) => `/blockers/${id}/escalate`,
        linkTasks: (id: string) => `/blockers/${id}/tasks`,
        unlinkTask: (id: string, taskId: string) => `/blockers/${id}/tasks/${taskId}`,
    },
    comments: {
        list: (sprintId: string) => `/sprints/${sprintId}/comments`,
        analytics: (sprintId: string) => `/sprints/${sprintId}/comments/analytics`,
        taskList: (taskId: string) => `/tasks/${taskId}/comments`,
        detail: (id: string) => `/comments/${id}`,
        create: (sprintId: string) => `/sprints/${sprintId}/comments`,
        createOnTask: (taskId: string) => `/tasks/${taskId}/comments`,
        update: (id: string) => `/comments/${id}`,
        respond: (id: string) => `/comments/${id}/respond`,
        delete: (id: string) => `/comments/${id}`,
    },
    decisions: {
        list: (sprintId: string) => `/sprints/${sprintId}/decisions`,
        analytics: (sprintId: string) => `/sprints/${sprintId}/decisions/analytics`,
        create: (sprintId: string) => `/sprints/${sprintId}/decisions`,
        detail: (id: string) => `/decisions/${id}`,
        update: (id: string) => `/decisions/${id}`,
        delete: (id: string) => `/decisions/${id}`,
    },
    redFlags: {
        list: (sprintId: string) => `/sprints/${sprintId}/red-flags`,
        count: (sprintId: string) => `/sprints/${sprintId}/red-flags/count`,
        create: (sprintId: string) => `/sprints/${sprintId}/red-flags`,
        detail: (id: string) => `/red-flags/${id}`,
        update: (id: string) => `/red-flags/${id}`,
        delete: (id: string) => `/red-flags/${id}`,
    },
    alerts: {
        list: (sprintId: string) => `/sprints/${sprintId}/alerts`,
        count: (sprintId: string) => `/sprints/${sprintId}/alerts/count`,
        create: (sprintId: string) => `/sprints/${sprintId}/alerts`,
        detail: (id: string) => `/alerts/${id}`,
        update: (id: string) => `/alerts/${id}`,
        delete: (id: string) => `/alerts/${id}`,
        acknowledge: (id: string) => `/alerts/${id}/acknowledge`,
        done: (id: string) => `/alerts/${id}/done`,
    },
    dashboard: {
        full: (sprintId: string) => `/sprints/${sprintId}/dashboard`,
        healthScore: (sprintId: string) => `/sprints/${sprintId}/dashboard/health-score`,
        metrics: (sprintId: string) => `/sprints/${sprintId}/dashboard/metrics`,
    },
    handoffs: {
        overview: "/handoffs",
        criteria: "/handoffs/criteria",
        criteriaCreate: "/handoffs/criteria",
        criteriaUpdate: (id: string) => `/handoffs/criteria/${id}`,
        criteriaDelete: (id: string) => `/handoffs/criteria/${id}`,
        toggleCheck: (taskId: string, criteriaId: string) => `/handoffs/checks/${taskId}/${criteriaId}`,
        taskStatus: (taskId: string) => `/handoffs/tasks/${taskId}`,
    },
    workload: {
        byProject: "/workload/by-project",
        bySprint: "/workload/by-sprint",
        byTeam: "/workload/by-team",
        byMember: "/workload/by-member",
    },
    ownership: {
        feed: "/ownership/feed",
        leaderboard: "/ownership/leaderboard",
        stats: "/ownership/stats",
    },
    features: {
        list: "/features",
        create: "/features",
        detail: (id: string) => `/features/${id}`,
        update: (id: string) => `/features/${id}`,
        delete: (id: string) => `/features/${id}`,
        linkTasks: (id: string) => `/features/${id}/tasks`,
        unlinkTask: (id: string, taskId: string) => `/features/${id}/tasks/${taskId}`,
    },
    reports: {
        full: (projectId: string) => `/reports/project/${projectId}`,
        overview: (projectId: string) => `/reports/project/${projectId}/overview`,
        delivery: (projectId: string) => `/reports/project/${projectId}/delivery`,
        workload: (projectId: string) => `/reports/project/${projectId}/workload`,
        handoff: (projectId: string) => `/reports/project/${projectId}/handoff`,
        authorship: (projectId: string) => `/reports/project/${projectId}/authorship`,
        friction: (projectId: string) => `/reports/project/${projectId}/friction`,
        recommendations: (projectId: string) => `/reports/project/${projectId}/recommendations`,
        export: (projectId: string, section: string) => `/reports/project/${projectId}/export/${section}`,
    },
    meetings: {
        list: "/meetings",
        listLite: "/meetings/list",
        create: "/meetings",
        detail: (id: string) => `/meetings/${id}`,
        update: (id: string) => `/meetings/${id}`,
        delete: (id: string) => `/meetings/${id}`,
        addSlot: (id: string) => `/meetings/${id}/slots`,
        removeSlot: (id: string, slotId: string) => `/meetings/${id}/slots/${slotId}`,
        vote: (id: string, slotId: string) => `/meetings/${id}/vote/${slotId}`,
        finalize: (id: string) => `/meetings/${id}/finalize`,
        rsvp: (id: string, userId: string) => `/meetings/${id}/attendees/${userId}/rsvp`,
        comments: (id: string) => `/meetings/${id}/comments`,
        commentDelete: (id: string, commentId: string) => `/meetings/${id}/comments/${commentId}`,
        attachments: (id: string) => `/meetings/${id}/attachments`,
        attachmentDelete: (id: string, attachmentId: string) => `/meetings/${id}/attachments/${attachmentId}`,
    },
    deploys: {
        list: "/deploys",
        create: "/deploys",
        detail: (id: string) => `/deploys/${id}`,
        download: (id: string) => `/deploys/${id}/download`,
        updateStatus: (id: string) => `/deploys/${id}/status`,
        delete: (id: string) => `/deploys/${id}`,
    },
    reminders: {
        list: "/reminders",
        create: "/reminders",
        detail: (id: string) => `/reminders/${id}`,
        update: (id: string) => `/reminders/${id}`,
        dismiss: (id: string) => `/reminders/${id}/dismiss`,
        reactivate: (id: string) => `/reminders/${id}/reactivate`,
        delete: (id: string) => `/reminders/${id}`,
        stats: "/reminders/stats",
    },
    gantt: {
        tasks: "/gantt/tasks",
    },
    deliveryAnalytics: {
        composite: "/analytics/delivery",
        velocity: "/analytics/delivery/velocity",
        throughput: "/analytics/delivery/throughput",
        onTime: "/analytics/delivery/on-time",
        health: "/analytics/delivery/health",
        friction: "/analytics/delivery/friction",
        blockerTrend: "/analytics/delivery/blocker-trend",
        leaderboard: "/analytics/delivery/leaderboard",
    },
    baseUrl: import.meta.env.VITE_API_URL as string,
};
